import { useState } from "react";
import { ArrowUpRight, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Reveal, Eyebrow } from "../components/Reveal";
import { ARTICLES } from "../data/content";

export const Insights = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="insights" data-testid="insights-section" className="bg-[#F3F4F6] py-24 lg:py-36 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <Reveal>
          <Eyebrow>Insights</Eyebrow>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mt-6">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.12] text-[#0A0F1D] max-w-2xl">
              Thinking from the translational front line.
            </h2>
            <p className="text-slate-600 text-base max-w-md leading-relaxed">
              Perspectives on leadership, development strategy, regulatory pathways and the
              organizational side of bringing science to patients.
            </p>
          </div>
        </Reveal>

        <div className="mt-16 grid md:grid-cols-2 gap-5" data-testid="insights-grid">
          {ARTICLES.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.07}>
              <button
                data-testid={`insight-card-${a.slug}`}
                onClick={() => setActive(a)}
                className="w-full h-full text-left bg-white border border-slate-200 p-8 sm:p-10 group hover:border-teal-600/40 hover:-translate-y-1 transition-[border-color,transform] duration-300 flex flex-col"
              >
                <div className="flex items-center justify-between gap-4">
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-teal-700 border border-teal-600/30 px-3 py-1">
                    {a.category}
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-slate-500">
                    <Clock className="w-3.5 h-3.5" /> {a.minutes} min read
                  </span>
                </div>
                <h3 className="mt-6 font-serif text-2xl sm:text-[26px] leading-tight text-[#0B132B] group-hover:text-teal-800 transition-colors duration-300">
                  {a.title}
                </h3>
                <p className="mt-4 text-sm text-slate-600 leading-relaxed flex-1">{a.excerpt}</p>
                <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-teal-700 group-hover:gap-3 transition-[gap] duration-300">
                  Read the perspective <ArrowUpRight className="w-4 h-4" />
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="max-w-2xl bg-[#F8F9FA] border-slate-200 max-h-[85vh] overflow-y-auto" data-testid="insight-article-modal" data-lenis-prevent>
          {active && (
            <>
              <DialogHeader>
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-teal-700">
                  {active.category} · {active.minutes} min read
                </span>
                <DialogTitle className="font-serif text-2xl sm:text-3xl leading-tight text-[#0B132B] mt-3">
                  {active.title}
                </DialogTitle>
              </DialogHeader>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mt-2">
                By Dr. Launa Aspeslet, PhD
              </p>
              <div className="mt-8 space-y-10">
                {active.sections.map((s, si) => (
                  <div key={si}>
                    {s.heading && (
                      <h4 className="font-serif text-xl sm:text-2xl text-[#0B132B] mb-4">{s.heading}</h4>
                    )}
                    <div className="space-y-5">
                      {s.paragraphs.map((p, pi) => (
                        <p key={pi} className="text-[15px] text-slate-700 leading-relaxed text-justify [hyphens:auto]">{p}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
